<template>
	<div class="teacher-analytics">
		<div class="teacher-analytics__header">
			<h1 class="teacher-analytics__title">Аналитика преподавателя</h1>
			<p class="teacher-analytics__subtitle">Статистика по вашим курсам</p>
		</div>

		<!-- Stats Cards -->
		<div class="teacher-analytics__stats">
			<div class="stat-card">
				<div class="stat-card__icon">📚</div>
				<div class="stat-card__content">
					<h3 class="stat-card__value">{{ stats.totalCourses || 0 }}</h3>
					<p class="stat-card__label">Всего курсов</p>
				</div>
			</div>

			<div class="stat-card">
				<div class="stat-card__icon">👥</div>
				<div class="stat-card__content">
					<h3 class="stat-card__value">{{ stats.totalStudents || 0 }}</h3>
					<p class="stat-card__label">Всего студентов</p>
				</div>
			</div>

			<div class="stat-card">
				<div class="stat-card__icon">✅</div>
				<div class="stat-card__content">
					<h3 class="stat-card__value">{{ stats.completedCourses || 0 }}</h3>
					<p class="stat-card__label">Завершено курсов</p>
				</div>
			</div>

			<div class="stat-card">
				<div class="stat-card__icon">⭐</div>
				<div class="stat-card__content">
					<h3 class="stat-card__value">{{ stats.averageRating || 'N/A' }}</h3>
					<p class="stat-card__label">Средний рейтинг</p>
				</div>
			</div>
		</div>

		<!-- Charts -->
		<div class="teacher-analytics__charts">
			<!-- Students Enrollment Chart -->
			<div class="chart-card">
				<h3 class="chart-card__title">Записи студентов по месяцам</h3>
				<div class="chart-card__content">
					<Line :data="enrollmentChartData" :options="chartOptions" />
				</div>
			</div>

			<!-- Course Completion Chart -->
			<div class="chart-card">
				<h3 class="chart-card__title">Завершение курсов</h3>
				<div class="chart-card__content">
					<Doughnut :data="completionChartData" :options="doughnutOptions" />
				</div>
			</div>
		</div>

		<!-- Courses Table -->
		<div class="teacher-analytics__section">
			<h2 class="teacher-analytics__section-title">Статистика по курсам</h2>
			<div v-if="loading" class="teacher-analytics__loading">
				<div class="spinner"></div>
				<p>Загрузка данных...</p>
			</div>
			<table v-else class="table">
				<thead>
					<tr>
						<th>Курс</th>
						<th>Студентов</th>
						<th>Завершено</th>
						<th>Средний прогресс</th>
						<th>Рейтинг</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="course in courses" :key="course.id">
						<td>
							<strong>{{ course.title }}</strong>
						</td>
						<td>{{ course.students_count || 0 }}</td>
						<td>{{ course.completed_count || 0 }}</td>
						<td>
							<div class="table__progress">
								<div class="table__progress-bar">
									<div
										class="table__progress-fill"
										:style="{ width: `${course.average_progress || 0}%` }"
									></div>
								</div>
								<span class="table__progress-text">
									{{ course.average_progress || 0 }}%
								</span>
							</div>
						</td>
						<td>
							<div class="table__rating">⭐ {{ course.rating || 'N/A' }}</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script>
import { Line, Doughnut } from 'vue-chartjs'
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	ArcElement,
	Title,
	Tooltip,
	Legend,
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Title, Tooltip, Legend)

export default {
	name: 'TeacherAnalytics',
	components: {
		Line,
		Doughnut,
	},
	data() {
		return {
			loading: false,
			stats: {
				totalCourses: 0,
				totalStudents: 0,
				completedCourses: 0,
				averageRating: 0,
			},
			courses: [],
			enrollmentChartData: {
				labels: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн'],
				datasets: [
					{
						label: 'Новые студенты',
						data: [12, 19, 15, 25, 22, 30],
						borderColor: '#0095ec',
						backgroundColor: 'rgba(0, 149, 236, 0.1)',
						tension: 0.4,
					},
				],
			},
			completionChartData: {
				labels: ['Завершено', 'В процессе', 'Не начато'],
				datasets: [
					{
						data: [45, 35, 20],
						backgroundColor: ['#28a745', '#ffc107', '#dc3545'],
					},
				],
			},
			chartOptions: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						display: true,
						position: 'bottom',
					},
				},
			},
			doughnutOptions: {
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						display: true,
						position: 'bottom',
					},
				},
			},
		}
	},
	mounted() {
		this.loadAnalytics()
	},
	methods: {
		async loadAnalytics() {
			this.loading = true
			try {
				// TODO: Implement API calls
				// Mock data for now
				this.stats = {
					totalCourses: 8,
					totalStudents: 245,
					completedCourses: 156,
					averageRating: 4.7,
				}

				this.courses = [
					{
						id: 1,
						title: 'Основы веб-разработки',
						students_count: 85,
						completed_count: 62,
						average_progress: 73,
						rating: 4.8,
					},
					{
						id: 2,
						title: 'JavaScript для начинающих',
						students_count: 72,
						completed_count: 48,
						average_progress: 67,
						rating: 4.6,
					},
					{
						id: 3,
						title: 'React.js продвинутый',
						students_count: 45,
						completed_count: 28,
						average_progress: 62,
						rating: 4.9,
					},
				]
			} catch (error) {
				console.error('Ошибка загрузки аналитики:', error)
			} finally {
				this.loading = false
			}
		},
	},
}
</script>

<style scoped>
.teacher-analytics {
	padding: 32px;
	max-width: 1400px;
	margin: 0 auto;
}

.teacher-analytics__header {
	margin-bottom: 32px;
}

.teacher-analytics__title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 32px;
	font-weight: 700;
	margin: 0 0 8px 0;
}

.teacher-analytics__subtitle {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 16px;
	margin: 0;
}

.teacher-analytics__stats {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
	gap: 24px;
	margin-bottom: 32px;
}

.stat-card {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 24px;
	display: flex;
	align-items: center;
	gap: 20px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	transition: all 0.3s ease;
}

.stat-card:hover {
	transform: translateY(-4px);
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.stat-card__icon {
	font-size: 48px;
}

.stat-card__content {
	flex: 1;
}

.stat-card__value {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 32px;
	font-weight: 700;
	margin: 0 0 4px 0;
}

.stat-card__label {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	margin: 0;
}

.teacher-analytics__charts {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
	gap: 24px;
	margin-bottom: 32px;
}

.chart-card {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 24px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.chart-card__title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 18px;
	font-weight: 600;
	margin: 0 0 20px 0;
}

.chart-card__content {
	height: 300px;
}

.teacher-analytics__section {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 24px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.teacher-analytics__section-title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 20px;
	font-weight: 600;
	margin: 0 0 20px 0;
}

.teacher-analytics__loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
}

.spinner {
	width: 40px;
	height: 40px;
	border: 4px solid #f3f3f3;
	border-top: 4px solid #0095ec;
	border-radius: 50%;
	animation: spin 1s linear infinite;
	margin-bottom: 16px;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.table {
	width: 100%;
	border-collapse: collapse;
}

.table th {
	text-align: left;
	padding: 12px;
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
	font-weight: 500;
	border-bottom: 2px solid #e8e8e8;
}

.table td {
	padding: 16px 12px;
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	border-bottom: 1px solid #e8e8e8;
}

.table__progress {
	display: flex;
	align-items: center;
	gap: 12px;
}

.table__progress-bar {
	flex: 1;
	height: 8px;
	background-color: #e8e8e8;
	border-radius: 4px;
	overflow: hidden;
}

.table__progress-fill {
	height: 100%;
	background-color: #0095ec;
	transition: width 0.3s ease;
}

.table__progress-text {
	min-width: 45px;
	color: #636363;
	font-size: 13px;
	font-weight: 500;
}

.table__rating {
	color: #ffc107;
	font-weight: 600;
}
</style>

